import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as moment from 'moment/moment';
import axios from 'axios';
import cx from 'classnames';
import translate from '../../../commonComponents/translate';
import ShopProductVariation from './ShopProductVariation';
import ProductProp, { PACK_TYPE } from '../propTypes/productProp';
import ShopProductImage from './ShopProductImage';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import PriceDisplay from '../../../commonComponents/PriceDisplay';
import ProductSubscription from './ShopProductSubscription';
import {
  compose,
  FrequencySubscriptionTypes,
  promotionEndingMessage,
} from '../../../services/utilityFunctions';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import User from '../../../../../../domain/models/user';
import PackVariations from './pack/PackVariations';
import { normalizeNumber } from '../../../services/numberUtilis';
import SocialLinks from '../../../commonComponents/social/SocialLinks';
import { SHARE_LINKS } from '../../../commonComponents/social/propTypes/shareLinkProps';
import AdvancedPack from './pack/AdvancedPack';
import withCSRF from '../../../commonComponents/withCSRF';
import Notifications from '../../../services/notifications';
import useCountdown from '../../../commonComponents/hooks/useCountdown';
import { formatDate } from '../../../services/dateUtilityFunctions';
import HybridPack from './pack/HybridPack';

function ShopProductContainer({
  t,
  product,
  changeProductQuantity,
  onPackQuantityChange,
  priceTotal,
  minorPriceTotal,
  totalVA,
  totalCV,
  addToCart,
  directSellingActive,
  onBuyNow,
  addToCartLocked,
  productItems,
  packQuantity,
  isMarketPlaceEnabled,
  packVariations,
  handleChangeVariations,
  _csrf,
}) {
  const { tenant } = useContext(ConfigsContext);
  const { directSellingSettings } = tenant?.settings || {};
  const { CVIntegration, VAIntegration } = directSellingSettings || {};
  const { loggedUser } = useContext(LoggedUserContext);

  const { isPack, pack } = product;
  const isNormalPack = useMemo(
    () => isPack && pack?.packType === PACK_TYPE.NORMAL_PACK,
    [isPack, pack?.packType],
  );

  const isAdvancedPack = useMemo(
    () => isPack && pack?.packType === PACK_TYPE.ADVANCED_PACK,
    [isPack, pack?.packType],
  );

  const isHybridPack = useMemo(
    () => isPack && pack?.packType === PACK_TYPE.HYBRID_PACK,
    [isPack, pack?.packType],
  );

  const [following, setFollowing] = useState(
    (product?.usersFollowing || []).includes(loggedUser?._id),
  );
  const [isLoading, setIsLoading] = useState(false);

  const [selectedImage, setSelectedImage] = useState({
    id: null,
    index: 0,
    isVideo: false,
  });
  const changeImage = id => () => setSelectedImage(id);
  const showVA =
    directSellingActive &&
    VAIntegration &&
    loggedUser?.type === User.TYPE.COLLABORATOR;
  const showCV =
    directSellingActive &&
    CVIntegration &&
    loggedUser?.type === User.TYPE.COLLABORATOR;

  const [subscriptionFrequency, setSubscriptionFrequency] = useState(
    product?.availableSubscriptionFrequency?.length > 0
      ? product?.availableSubscriptionFrequency[0]
      : FrequencySubscriptionTypes.ONLY_ONCE,
  );
  const handleSubscriptionCheckbox = field => {
    setSubscriptionFrequency(field);
  };

  const stockManagementMessage = (
    variation,
    stockInCart,
    isPackProduct = false,
  ) => {
    const stockChosen = isPackProduct
      ? stockInCart * variation.quantity
      : stockInCart;
    // only show a message when the stock is managed by us, not null
    if (variation?.stockQuantity && variation?.stockQuantity >= 0) {
      const { restockDate, stockQuantity, restockInterval, lowStockWarning } =
        variation || {};
      let message = '';
      let date = null;
      if (restockDate) {
        date =
          new Date().getTime() > new Date(restockDate).getTime()
            ? null
            : moment.utc(restockDate).format('DD MMM, YY');
      }
      if (stockQuantity === 0) {
        const restockIntervalMsg = restockInterval
          ? t('shop.product.warning.message.noStock.interval', restockInterval)
          : t('shop.product.warning.message.noStock');
        message = date
          ? t('shop.product.warning.message.noStock.date', date)
          : restockIntervalMsg;
        return (
          <span
            data-testid="stock-error-first"
            className={
              date || restockInterval ? 'warning-yellow' : 'warning-red'
            }
          >
            {message}
          </span>
        );
      }
      if (variation.stockQuantity < lowStockWarning) {
        message = t('shop.product.warning.message.lowStock');
      }
      if (variation.stockQuantity < stockChosen) {
        const restockIntervalMsg = restockInterval
          ? t(
              'shop.product.warning.message.quantityAboveStock.interval',
              restockInterval,
            )
          : t('shop.product.warning.message.warning.noStock');
        // t('shop.product.warning.message.quantityAboveStock.noDateNoInterval');
        message = date
          ? t('shop.product.warning.message.quantityAboveStock.date', date)
          : restockIntervalMsg;
      }
      return (
        <span
          data-testid="stock-management"
          className={
            !date && !restockInterval ? 'warning-red' : 'warning-yellow'
          }
        >
          {message}
        </span>
      );
    }
    return <span />;
  };

  let userPriceTotal =
    isPack && !product?.pack?.userPromotionPrice && priceTotal > 0
      ? {
          originalPriceWithVAT:
            (product?.pack?.userPrice?.originalPriceWithVAT ?? 0) *
            packQuantity,
          originalPriceWithoutVAT:
            (product?.pack?.userPrice?.originalPriceWithoutVAT ?? 0) *
            packQuantity,
          code: product?.pack?.userPrice?.code || 'eur',
          billingType: product?.pack?.userPrice?.billingType,
          highlightedPrice: product?.pack?.userPrice?.highlightedPrice,
        }
      : {
          originalPriceWithVAT: isPack
            ? (product?.pack?.userPrice?.originalPriceWithVAT ?? 0) *
              packQuantity
            : priceTotal * 100,
          originalPriceWithoutVAT: isPack
            ? (product?.pack?.userPrice?.originalPriceWithoutVAT ?? 0) *
              packQuantity
            : minorPriceTotal * 100,
          code: product?.variations[0]?.userPrice?.code || 'eur',
          billingType: product?.variations[0]?.userPrice?.billingType,
          highlightedPrice: product?.variations[0]?.userPrice?.highlightedPrice,
        };

  const userPromotionPriceTotal =
    isPack && product?.pack?.userPromotionPrice && priceTotal > 0
      ? {
          originalPriceWithVAT: priceTotal * 100,
          originalPriceWithoutVAT: minorPriceTotal * 100,
          code: product?.variations[0]?.userPrice?.code || 'eur',
          billingType: product?.variations[0]?.userPrice?.billingType,
          highlightedPrice: product?.variations[0]?.userPrice?.highlightedPrice,
        }
      : {};

  if (product?.pack?.packType === PACK_TYPE.HYBRID_PACK) {
    userPriceTotal = {
      originalPriceWithVAT: priceTotal * 100,
      packQuantity,
      originalPriceWithoutVAT: minorPriceTotal * 100,
      code: product?.pack?.userPrice?.code || 'eur',
      billingType: product?.pack?.userPrice?.billingType,
      highlightedPrice: product?.pack?.userPrice?.highlightedPrice,
    };
  }

  const linksOptions = useMemo(() => {
    if (loggedUser?._id && isMarketPlaceEnabled) {
      return [{ key: SHARE_LINKS.SHARE_LINK, marketProductId: product._id }];
    }
    return [];
  }, [loggedUser?._id, isMarketPlaceEnabled]);

  const media = useMemo(
    () =>
      product?.teaserVideo
        ? [
            {
              ...product?.teaserVideo,
              isVideo: true,
              _id: product?.teaserVideo?.name,
            },
            ...(product.images || []),
          ]
        : product.images,
    [product?.teaserVideo, product.images],
  );

  const handleFollowProduct = async () => {
    try {
      setIsLoading(true);
      await axios.put(
        `/api/market/product/${product?._id}/${
          following ? 'unfollow' : 'follow'
        }`,
        {
          user: loggedUser?._id,
        },
        {
          headers: {
            'X-Gamification-API': 'Games',
            'Accept-Version': '1.0.0',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );

      setFollowing(prevState => !prevState);
      Notifications.showNotificationSuccess(
        t('success'),
        following
          ? t('product.unfollowing.success')
          : t('product.following.success'),
      );
    } catch (err) {
      Notifications.showNotificationError(
        err,
        following
          ? t('product.unfollowing.error')
          : t('product.following.error'),
      );
    } finally {
      setIsLoading(false);
    }
  };
  const followingCx = cx(
    'follow-btn',
    { 'm-loader': isLoading },
    { 'm-loader--right': isLoading },
    { 'm-loader--light': isLoading },
  );

  // region Manages the Initial Drop Date
  const availableFromMs = product?.availableFrom
    ? Number(product.availableFrom)
    : null;

  const isPastStartDate = availableFromMs ? availableFromMs < Date.now() : true;

  const formattedStartDate = useMemo(() => {
    if (!availableFromMs) return null;
    return formatDate(availableFromMs);
  }, [availableFromMs]);
  // endregion Manages the Initial Drop Date

  const availableUntilMs = product?.availableUntil
    ? Number(product.availableUntil)
    : null;

  const { isOver, hh, mm, ss } = useCountdown(availableUntilMs);

  return (
    <div className="m-portlet m-portlet--rounded m-portlet--rounded-force m--margin-top-10 m--margin-bottom-10 shop-product">
      <div className="m-portlet__body">
        <div className="row no-padding">
          <div className="col-lg-6 col-md-4">
            <div className="productImageContainer">
              {media.length > 1 && (
                <div className="product-thumbnail" id="product-thumbnail">
                  {media?.map((image, index) => (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                    <div
                      onClick={changeImage({ id: image._id, index })}
                      className="product-thumbnail-item"
                      key={`thumb-${image._id}`}
                    >
                      <img
                        src={
                          image?.isVideo
                            ? image?.thumb || '/images/no_image_upload.png'
                            : image.url || '/images/no_image_upload.png'
                        }
                        alt=""
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className="product-img">
                <ShopProductImage
                  product={product}
                  selectedImage={selectedImage.id}
                  media={media}
                  showVideoFirst
                />
              </div>
            </div>
            <div className="product-share-links">
              <SocialLinks linksOptions={linksOptions} />
            </div>
          </div>
          <div className="col-lg-6 col-md-8">
            <div className="drop">
              {!isPastStartDate && (
                <div className="start-date-block">
                  <p className="typo-bold">{t('sessions_startDate')}</p>

                  <div className="badge badge--primary">
                    <i className="fa fa-lock icon-16 text-dynamic" />

                    <p className="text-dynamic">{formattedStartDate}</p>
                  </div>
                </div>
              )}

              {availableUntilMs && !isOver && isPastStartDate && (
                <div className="countdown">
                  <div className="countdown__row">
                    <p className="pill pill--primary text-dynamic">{hh}h</p>
                    <span>:</span>
                    <p className="pill pill--primary text-dynamic">{mm}m</p>
                    <span>:</span>
                    <p className="pill pill--primary text-dynamic">{ss}s</p>
                  </div>

                  <div className="countdown__meta">
                    <i className="fa fa-clock icon-16 text-dynamic" />

                    <p className="typo-semibold">
                      {t('product.availability.ending')}
                    </p>
                  </div>
                </div>
              )}

              {availableUntilMs && isOver && (
                <div className="closed">
                  <i className="fa fa-window-close icon-16 text-dynamic" />

                  <p className="typo-semibold">
                    {t('product.availability.closed')}
                  </p>
                </div>
              )}
            </div>

            <div className="product-detail-name-wrapper">
              <div className="product-name">
                <div className="product-title">
                  <h2>{product.name}</h2>
                </div>

                {product.sku && (
                  <div
                    data-testid={`sku-${product._id}`}
                    className="product-sku"
                  >
                    {`${t('sku')}: ${product.sku}`}
                  </div>
                )}
              </div>

              {loggedUser?._id && (
                <button
                  className={followingCx}
                  type="button"
                  onClick={handleFollowProduct}
                >
                  <i
                    className={following ? 'fa fa-bell-slash' : 'fa fa-bell'}
                  />
                  <span>
                    {following
                      ? t('product.unfollow.products')
                      : t('product.follow.products')}
                  </span>
                </button>
              )}
            </div>

            {product.summary && (
              <div
                data-testid={`summary-${product._id}`}
                className="product-summary"
              >
                {product.summary}
              </div>
            )}
            <div className="product-info">
              {!isPack &&
                product.variations.map(
                  (v, index) =>
                    v.isPublic && (
                      <ShopProductVariation
                        key={`variation-${v._id}`}
                        index={index}
                        variation={v}
                        t={t}
                        changeProductQuantity={changeProductQuantity}
                        productItems={productItems}
                        stockManagementMessage={stockManagementMessage}
                        quantityLockedToOne={
                          !!product?.externalPaymentLink &&
                          !product?.externalPaymentAllowQuantity
                        }
                      />
                    ),
                )}
              {(isNormalPack || isHybridPack) && (
                <PackVariations
                  product={product}
                  stockManagementMessage={stockManagementMessage}
                  index={1}
                  t={t}
                  onPackQuantityChange={onPackQuantityChange}
                  packQuantity={packQuantity}
                  isHybridPack={isHybridPack}
                />
              )}
              {isAdvancedPack && (
                <AdvancedPack
                  packVariations={packVariations}
                  onChangeVariations={handleChangeVariations}
                  product={product}
                  stockManagementMessage={variation =>
                    stockManagementMessage(
                      variation,
                      packQuantity,
                      isAdvancedPack,
                    )
                  }
                  onPackQuantityChange={onPackQuantityChange}
                  packQuantity={packQuantity}
                />
              )}

              {isHybridPack && (
                <HybridPack
                  packVariations={packVariations}
                  onChangeVariations={handleChangeVariations}
                  product={product}
                  stockManagementMessage={variation =>
                    stockManagementMessage(
                      variation,
                      packQuantity,
                      isAdvancedPack,
                    )
                  }
                  onPackQuantityChange={onPackQuantityChange}
                  packQuantity={packQuantity}
                />
              )}

              {product.availableForSubscription && (
                <div className="subscription">
                  <ProductSubscription
                    t={t}
                    product={product}
                    subscriptionFrequency={subscriptionFrequency}
                    handleSubscriptionCheckbox={handleSubscriptionCheckbox}
                  />
                </div>
              )}
              <div className="price row">
                <div className="price-totals">
                  {showVA && (
                    <div className="totalVA">{`${t(
                      'shop.product.totalVA',
                    )}: ${normalizeNumber(totalVA, 2)}`}</div>
                  )}
                  {showCV && (
                    <div className="totalCV">{`${t(
                      'shop.product.totalCV',
                    )}: ${normalizeNumber(totalCV, 2)}`}</div>
                  )}
                  {isPack ? (
                    <div className="variation-promotionEnding pack-variation-promotionEnding">
                      {promotionEndingMessage(
                        product?.pack?.promotionPriceDateEnd,
                        'product.promotion.days.2',
                      )}
                    </div>
                  ) : null}
                </div>
                <PriceDisplay
                  t={t}
                  userPrice={userPriceTotal}
                  userPromotionPrice={userPromotionPriceTotal}
                  showFree={false}
                  withWrapper
                />
              </div>
            </div>

            {((!isOver && isPastStartDate) ||
              (!product?.availableFrom && !product?.availableUntil)) && (
              <div className="product-actions row">
                {subscriptionFrequency ===
                  FrequencySubscriptionTypes.ONLY_ONCE && (
                  <>
                    {/* External-payment-link products are paid on an external
                        Stripe checkout with a fixed price/quantity — Buy Now
                        only, no cart mixing (BRAVON-6316). */}
                    {!product?.externalPaymentLink && (
                      <span className="col-5">
                        <button
                          type="button"
                          onClick={addToCart}
                          disabled={addToCartLocked}
                          className="btn m-btn--pill btn-theme btn-lg m-btn m-btn--custom cart-button"
                        >
                          <i className="la la-shopping-cart" />
                          <span>{t('shop.addToCart')}</span>
                        </button>
                      </span>
                    )}
                    <span
                      className={
                        product?.externalPaymentLink ? 'col-12' : 'col-7'
                      }
                    >
                      <a
                        href=""
                        className={`btn m-btn--pill btn-theme btn-lg m-btn m-btn--custom ${
                          addToCartLocked && 'disabled'
                        }`}
                        itemProp="url"
                        onClick={event => onBuyNow(event, null)}
                      >
                        {t('shop.buyNow')}
                      </a>
                    </span>
                  </>
                )}
                {subscriptionFrequency !==
                  FrequencySubscriptionTypes.ONLY_ONCE && (
                  <>
                    {!product?.externalPaymentLink && (
                      <span className="col-5">
                        <button
                          type="button"
                          onClick={event =>
                            addToCart(event, subscriptionFrequency)
                          }
                          disabled={addToCartLocked}
                          className="btn m-btn--pill btn-theme btn-lg m-btn m-btn--custom cart-button"
                        >
                          <i className="la la-shopping-cart" />
                          <span>{t('shop.addToCart')}</span>
                        </button>
                      </span>
                    )}
                    <span
                      className={
                        product?.externalPaymentLink ? 'col-12' : 'col-7'
                      }
                    >
                      <button
                        type="button"
                        disabled={addToCartLocked}
                        onClick={event =>
                          onBuyNow(event, subscriptionFrequency)
                        }
                        className={`btn m-btn--pill btn-theme btn-lg m-btn m-btn--custom ${
                          addToCartLocked && 'disabled'
                        }`}
                      >
                        <span>{t('shop.subscribe')}</span>
                      </button>
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

ShopProductContainer.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductProp.propType).isRequired,
  changeProductQuantity: PropTypes.func.isRequired,
  onPackQuantityChange: PropTypes.func.isRequired,
  addToCart: PropTypes.func.isRequired,
  onBuyNow: PropTypes.func.isRequired,
  priceTotal: PropTypes.number.isRequired,
  minorPriceTotal: PropTypes.number.isRequired,
  totalVA: PropTypes.number.isRequired,
  totalCV: PropTypes.number.isRequired,
  directSellingActive: PropTypes.bool.isRequired,
  addToCartLocked: PropTypes.bool.isRequired,
  productItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      amount: PropTypes.number,
      price: PropTypes.number,
      va: PropTypes.number,
      commissionable: PropTypes.bool,
    }),
  ).isRequired,
  packQuantity: PropTypes.number.isRequired,
  isMarketPlaceEnabled: PropTypes.bool,
  packVariations: PropTypes.arrayOf(
    PropTypes.shape({
      variation: PropTypes.string,
      quantity: PropTypes.number,
    }),
  ),
  handleChangeVariations: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

ShopProductContainer.defaultProps = {
  isMarketPlaceEnabled: false,
  packVariations: [],
};

export default compose(translate, withCSRF)(ShopProductContainer);
